<template>
  <section class="faq-section py-5">
    <div class="container px-3">
      <h2 class="section-title display-4 text-center mb-5 reveal-title font-good-brush">Preguntas Frecuentes</h2>
      
      <div class="faq-container d-flex flex-column align-items-center justify-content-center">  
        <div class="faq-content d-flex flex-column align-items-center justify-content-center">
          <div class="accordion" id="faqAccordion">
            <div class="accordion-item reveal-item" v-for="(faq, index) in faqs" :key="index">
              <h3 class="accordion-header">
                <button 
                  class="accordion-button" 
                  :class="{ 'collapsed': !faq.isOpen }"
                  type="button"
                  @click="toggleFaq(index)"
                >
                  <span class="question-text">{{ faq.question }}</span>
                  <span class="accordion-icon"></span>
                </button>
              </h3>
              <div 
                class="accordion-collapse"
                :class="{ 'show': faq.isOpen }"
                :style="{ maxHeight: faq.isOpen ? getMaxHeight(index) : '0px' }"
              >
                <div class="accordion-body" v-html="faq.answer"></div>
              </div>
            </div>
          </div>
        </div>
      </div>
      <div class="d-flex justify-content-center align-items-center flex-column">
        <p class="text-black">
          ¿No encontraste la respuesta que buscabas?
        </p>
        <a href="/contacto" class="btn btn-primary">
          <FaIcon name="question-circle" class="me-2" />
          Escribinos
        </a>
      </div>
    </div>
  </section>
</template>

<script setup>
import { ref, onMounted, nextTick } from 'vue'
import ScrollReveal from 'scrollreveal'

const faqs = ref([
  {
    question: "¿Venden derivados de cannabis?",
    answer: "No, somos una tienda que abastece productos de cultivo (Tierra, Fertilizantes, Macetas, etc) y artículos para el fumador. No vendemos semillas, esquejes, aceites, ni ningún otro derivado del cannabis.",
    isOpen: false
  },
  {
    question: "¿Hay un mínimo de compra?",
    answer: "Si, tenemos un minimo de compra de $4.000 pesos.",
    isOpen: false
  },
  {
    question: "¿Debo registrarme para realizar un pedido?",
    answer: "Si, es necesario registrarse para poder realizar un pedido. Esto nos permite llevar un control de las compras y ofrecerte un mejor servicio.",
    isOpen: false
  },
  {
    question: "¿Cuáles son los medios de pago?",
    answer: "Podes pagar en efectivo, con tarjeta de crédito o débito, transferencia bancaria o Mercado Pago. Si pagas con tarjeta de crédito, tené en cuenta que el monto mínimo es de $4.000.",
    isOpen: false
  },
  {
    question: "¿Realizan envíos?",
    answer: "Sí! A todo el país. Realizamos envios através de Andreani o Correo Argentino.",
    isOpen: false
  },
  {
    question: "¿En cuanto tiempo despachan?",
    answer: "Una vez realizado el pago y corroborado que no tenemos faltante de mercadería, realizamos el despacho dentro de las 72 hs. Cabe destacar que al interior del país una vez realizado el despacho llegara a destino dentro de los próximos 10 días.",
    isOpen: false
  },
])

const toggleFaq = (index) => {
  faqs.value[index].isOpen = !faqs.value[index].isOpen
}

const getMaxHeight = (index) => {
  // Provide enough space for the content
  return faqs.value[index].answer.length > 200 ? '400px' : '200px'
}

onMounted(() => {
  nextTick(() => {
    // Check if we're on mobile
    const isMobile = window.innerWidth < 768;
    
    // On mobile, show elements immediately without animation
    if (isMobile) {
      document.querySelectorAll('.faq-section .reveal-title, .faq-section .reveal-item').forEach(el => {
        el.style.opacity = '1';
        el.style.transform = 'none';
        el.style.visibility = 'visible';
      });
      return;
    }
    
    // Only initialize ScrollReveal on desktop
    setTimeout(() => {
      initializeScrollReveal();
    }, 50);
  });
});

const initializeScrollReveal = () => {
  // Detect if we're on mobile
  const isMobile = window.innerWidth < 768;
  
  // Don't initialize ScrollReveal on mobile devices
  if (isMobile) {
    console.log('ScrollReveal disabled on mobile devices');
    // Remove animation classes on mobile to prevent any visual issues
    document.querySelectorAll('.faq-section .reveal-title, .faq-section .reveal-item').forEach(el => {
      el.style.opacity = '1';
      el.style.transform = 'none';
      el.style.visibility = 'visible';
    });
    return;
  }
  
  // Initialize ScrollReveal only for desktop
  const sr = ScrollReveal({
    origin: 'bottom',
    distance: '50px',
    duration: 800,
    delay: 200,
    easing: 'cubic-bezier(0.5, 0, 0, 1)',
    reset: false,
    mobile: false,
    viewFactor: 0.2
  });

  // Reveal the title with different animation
  sr.reveal('.faq-section .reveal-title', {
    origin: 'top',
    distance: '30px',
    duration: 1000,
    delay: 100,
  });
  
  // Reveal each FAQ item with staggered effect
  const accordionItems = document.querySelectorAll('.faq-section .reveal-item');
  accordionItems.forEach((item, index) => {
    sr.reveal(item, {
      delay: 200 + (index * 150),
      distance: '40px',
      interval: 100,
    });
  });
  
  // Handle window resize - disable/enable based on viewport width
  window.addEventListener('resize', () => {
    if (window.innerWidth < 768 && !isMobile) {
      // Changed to mobile, disable ScrollReveal
      sr.destroy();
      document.querySelectorAll('.faq-section .reveal-title, .faq-section .reveal-item').forEach(el => {
        el.style.opacity = '1';
        el.style.transform = 'none';
        el.style.visibility = 'visible';
      });
    } else if (window.innerWidth >= 768 && isMobile) {
      // Changed to desktop, needs a refresh to re-enable
      location.reload();
    }
  });
  
  console.log('FAQ ScrollReveal initialized for desktop only');
}
</script>

<style scoped>
.faq-section {
  padding: 60px 0;
  background-color: var(--black-color);
}

.section-title {
  color: var(--white-color);
  font-weight: 700;
  letter-spacing: 0.5px;
  margin-bottom: 2.5rem;
  position: relative;
  text-transform: uppercase;
}

.section-title:after {
  content: '';
  position: absolute;
  width: 80px;
  height: 4px;
  background: var(--primary-color);
  bottom: -10px;
  left: 50%;
  transform: translateX(-50%);
  border-radius: 2px;
}

.faq-container {
  display: grid;
  grid-template-columns: 1fr 1.5fr;
  gap: 3rem;
  align-items: start;
}

.faq-image {
  position: sticky;
  top: 100px;
}

.faq-img {
  width: 100%;
  max-width: 500px;
  height: auto;
  border-radius: 20px;
  box-shadow: 0 15px 30px rgba(0, 0, 0, 0.15);
  transition: transform 0.5s ease;
  display: block;
}

.faq-img:hover {
  transform: translateY(-10px);
}

.faq-img-mobile {
  width: 80%;
  max-width: 300px;
  height: auto;
  margin: 0 auto 2rem;
  display: none;
  border-radius: 20px;
  box-shadow: 0 10px 20px rgba(0, 0, 0, 0.1);
}

.faq-content {
  max-width: 1000px;
}

.accordion-item {
  background: var(--black-color-2);
  border: 1px solid rgba(255,255,255,0.1);
  border-radius: 12px;
  margin-bottom: 1.2rem;
  overflow: hidden;
  box-shadow: 0 5px 15px rgba(0, 0, 0, 0.2);
  transition: all 0.3s ease;
}

.accordion-item:hover {
  box-shadow: 0 8px 20px rgba(0, 0, 0, 0.3);
}

.accordion-button {
  background: var(--black-color-2);
  color: var(--white-color);
  padding: 1.25rem;
  font-size: 1.1rem;
  font-weight: 600;
  border: none;
  width: 100%;
  text-align: left;
  transition: all 0.3s ease;
  position: relative;
  display: flex;
  align-items: center;
  justify-content: space-between;
}

.question-text {
  flex-grow: 1;
  padding-right: 15px;
}

.accordion-button:not(.collapsed) {
  background: var(--primary-color);
  color: var(--white-color);
}

.accordion-button:hover {
  background: var(--primary-color);
  color: var(--white-color);
}

.accordion-icon {
  position: relative;
  width: 20px;
  height: 20px;
  flex-shrink: 0;
  margin-left: auto;
}

.accordion-icon:before,
.accordion-icon:after {
  content: '';
  position: absolute;
  background-color: currentColor;
  transition: transform 0.3s ease;
}

.accordion-icon:before {
  top: 50%;
  left: 0;
  width: 100%;
  height: 2px;
  transform: translateY(-50%);
}

.accordion-icon:after {
  top: 0;
  left: 50%;
  width: 2px;
  height: 100%;
  transform: translateX(-50%);
}

.accordion-button.collapsed .accordion-icon:after {
  transform: translateX(-50%) rotate(0);
}

.accordion-button:not(.collapsed) .accordion-icon:after {
  transform: translateX(-50%) rotate(90deg);
  opacity: 0;
}

/* Remove any potential bootstrap styles that might interfere */
.accordion-button::after {
  display: none !important;
  content: none !important;
  background-image: none !important;
}

.accordion-body {
  padding: 1.5rem;
  color: var(--white-color-2);
  line-height: 1.7;
  font-size: 1rem;
  background: var(--black-color-3);
}

.accordion-collapse {
  overflow: hidden;
  transition: max-height 0.5s cubic-bezier(0.4, 0, 0.2, 1);
}

@media (max-width: 992px) {
  .faq-container {
    grid-template-columns: 1fr;
    gap: 2rem;
  }

  .faq-image {
    display: none;
  }

  .faq-img-mobile {
    display: block;
  }

  .faq-content {
    max-width: 100%;
  }

  .section-title {
    font-size: 2rem;
  }
}

@media (max-width: 768px) {
  .accordion-button {
    font-size: 1rem;
    padding: 1rem;
  }
  
  .accordion-body {
    padding: 1rem;
    font-size: 0.95rem;
  }

  .section-title {
    font-size: 1.75rem;
  }

  .faq-section {
    padding: 40px 0;
  }
}
</style>
